import {
  Close,
  Person,
  Phone,
  Business,
  CalendarToday,
  Event,
  Description,
  CheckCircle,
  Info,
  Receipt,
  AttachMoney,
} from "@mui/icons-material";
import {
  Stack,
  Button,
  Box,
  Typography,
  useTheme,
  Grid,
  alpha,
} from "@mui/material";
import React from "react";
import { RightPanel } from "../../../../../components/RightPanel";
import { formatDateTimeWithLocale } from "../../../../../helpers";
import { IBooking } from "../../../../../redux/booking/booking.types";
import { Booking } from "../../../../Inventory/ViewOccupancy";
import { PaymentDetailsDialog } from "../../../../leads/components/PaymentDetails/PaymentDetailsDialog";
import { useBillingCompanyContext } from "../../../../../providers";
import {
  fetchDriveStatusListAsync,
  upsertLeadAsync,
} from "../../../../../redux/leads/leadsActions";
import {
  closeLoaderWithMessage,
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../../../../../redux/messages/messagesActions";
import { useDispatchWrapper } from "../../../../../hooks";
import { useSelector } from "react-redux";
import { IStoreState } from "../../../../../redux/initialStoreState";
import { LoadState } from "../../../../../constants/enums";
import { useTimezoneFormatter } from "../../../../../hooks/useTimezoneFormatter";
import { LeadStatusRenderer } from "../../../../../components/LeadStatusRenderer/LeadStatusRenderer";

interface IViewBookingDetailsRightPanelProps {
  open: boolean;
  data: Booking | IBooking | any;
  onClose: () => void;
  onCancellationClick?: () => void;
  onComplete?: () => void;
}

const getFieldIcon = (key: string): React.ElementType => {
  const iconMap: Record<string, React.ElementType> = {
    status: CheckCircle,
    booking_id: Receipt,
    booking_unique_id: Receipt,
    customer_name: Person,
    customer_mobile_no: Phone,
    customer_email: Info,
    venue_name: Business,
    shift_time: Event,
    event_time: Event,
    from_date: CalendarToday,
    to_date: CalendarToday,
    total_price: AttachMoney,
    food_menu: Description,
    created_by_name: Person,
    modified_by_name: Person,
    create_ts: CalendarToday,
    insert_ts: CalendarToday,
  };

  return iconMap[key.toLowerCase()] || Info;
};

const FIELD_LABEL_OVERRIDES: Record<string, string> = {
  create_ts: "Created On",
  from_date: "Event Date",
  to_date: "Event End Date",
  total_price: "Total Amount",
  handled_by_employee: "Last Handled By Employee",
  created_by_name: "Created By",
  function_date: "Event Date",
  guest_budget: "Budget"
};

const formatFieldName = (key: string): string => {
  if (FIELD_LABEL_OVERRIDES[key]) {
    return FIELD_LABEL_OVERRIDES[key];
  }
  return key
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

const formatFieldValue = (key: string, value: any, ftDate: any): string => {
  if (value === null || value === undefined || value === "") {
    return "--";
  }
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  if (typeof value === "object") {
    return JSON.stringify(value);
  }

  const lowerKey = key.toLowerCase();
  if (lowerKey.includes("date") || lowerKey.includes("_ts")) {
    if (lowerKey.includes("_ts")) {
      return formatDateTimeWithLocale(String(value));
    } else {
      return ftDate(String(value)) || String(value);
    }
  }

  return String(value);
};

const shouldExcludeField = (key: string): boolean => {
  const lowerKey = key.toLowerCase();
  return lowerKey.includes("_uuid") || lowerKey.endsWith("uuid");
};

const SectionHeader: React.FC<{ title: string }> = ({ title }) => {
  const theme = useTheme();
  return (
    <Typography
      variant="subtitle2"
      sx={{
        fontWeight: 600,
        color: theme.palette.primary.main,
        textTransform: "uppercase",
        letterSpacing: 0.5,
        fontSize: "0.7rem",
        mb: 1,
      }}
    >
      {title}
    </Typography>
  );
};

const InfoItem: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string | number | null | undefined;
}> = ({ icon, label, value }) => {
  const theme = useTheme();
  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 0.5 }}>
      <Box sx={{ color: theme.palette.primary.main, display: "flex" }}>
        {icon}
      </Box>
      <Typography
        variant="caption"
        sx={{ color: theme.palette.text.secondary, minWidth: 90 }}
      >
        {label}:
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 500,
          color: value
            ? theme.palette.text.primary
            : theme.palette.text.disabled,
        }}
      >
        {value || "—"}
      </Typography>
    </Stack>
  );
};

const BOOKING_DETAILS_SECTIONS = [
  {
    title: "Customer Information",
    fields: ["customer_name", "customer_email", "customer_mobile_no", "create_ts"],
  },
  {
    title: "Event Information",
    fields: [
      "event_type",
      "event_time",
      "from_date",
      "venue_name",
    ],
  },
  {
    title: "Additional Information",
    fields: [
      "number_of_guests",
      "budget",
      "handled_by_employee",
      "created_by_name",
    ],
  },
];

export const BookingDetailsDisplay: React.FC<
  IViewBookingDetailsRightPanelProps
> = ({ open, data, onClose, onCancellationClick, onComplete }) => {
  const theme = useTheme();
  const { ftDate } = useTimezoneFormatter();
  const dispatch = useDispatchWrapper();
  const companyOutlet = useBillingCompanyContext();

  const { list: driveStatusList, loading: driveStatusLoading } = useSelector(
    (storeState: IStoreState) => storeState.leads.driveStatusList,
  );

  const [openCancellation, setOpenCancellation] = React.useState(false);

  // Fetch drive status list on mount
  React.useEffect(() => {
    if (
      companyOutlet.comapny.company_outlet_uuid &&
      driveStatusLoading === LoadState.NotLoaded
    ) {
      dispatch(
        fetchDriveStatusListAsync(companyOutlet.comapny.company_outlet_uuid),
      );
    }
  }, [companyOutlet.comapny.company_outlet_uuid, driveStatusLoading, dispatch]);

  const handleCancellationClick = () => {
    if (onCancellationClick) {
      onCancellationClick();
    } else if (data?.leads_uuid) {
      setOpenCancellation(true);
    }
  };

  const handleCancellationComplete = async (
    isSuccess: boolean,
    cancellationData?: {
      remarks: string;
      cancellation_dead_reason: string;
      leads_uuid: string;
    },
  ) => {
    if (!cancellationData) {
      dispatch(
        showMessage({
          type: "error",
          message: "Failed to fetch DEAD status. Please try again.",
          displayAs: "snackbar",
        }),
      );
      return;
    }
    try {
      dispatch(saveLoaderProgress());

      // Find the DEAD status from Redux store
      const deadStatus = driveStatusList.find(
        (item: any) => item.actual_status === "DEAD",
      );

      if (!deadStatus) {
        dispatch(
          showMessage({
            type: "error",
            message: "DEAD status not found. Please try again.",
            displayAs: "snackbar",
          }),
        );
        dispatch(saveLoaderCompleted());
        return;
      }

      const payloadArray = {
        ...data,
        company_outlet_uuid: companyOutlet.comapny.company_outlet_uuid,
        company_outlet_name: companyOutlet.comapny.company_outlet_name,
        cancellation_dead_reason: cancellationData?.cancellation_dead_reason,
        remarks: cancellationData?.remarks,
        drive_status_uuid: deadStatus.status_uuid,
        derived_status: deadStatus.actual_status,
        status: "DEAD",
      };

      dispatch(
        upsertLeadAsync(payloadArray, data, true, false, (isSuccess, data) => {
          dispatch(closeLoaderWithMessage());
          if (isSuccess) {
            onComplete?.();
          }
        }),
      );
    } catch (error) {
      dispatch(closeLoaderWithMessage());
      dispatch(
        showMessage({
          type: "error",
          message: "Failed to update status. Please try again.",
          displayAs: "snackbar",
        }),
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

  return (
    <RightPanel
      heading="View Booking Details"
      open={open}
      width="80%"
      onClose={onClose}
      actionButtons={() => (
        <Box display={"flex"} justifyContent={"space-between"} width="100%">
          <Stack direction={"row"} spacing={2} justifyContent={"flex-start"}>
            <Button variant="contained" onClick={onClose}>
              <Close sx={{ fontSize: 18, mr: 0.5 }} />
              Close
            </Button>
          </Stack>
          <Stack direction={"row"} spacing={2} justifyContent={"flex-end"}>
            {data?.status?.toUpperCase() !== "DEAD" && data?.leads_uuid && (
              <Button
                variant="contained"
                sx={{
                  backgroundColor: "error.main",
                  "&:hover": {
                    backgroundColor: "error.dark",
                  },
                }}
                onClick={handleCancellationClick}
              >
                Cancellation
              </Button>
            )}
          </Stack>
        </Box>
      )}
    >
      <Box sx={{
        position: "relative",
        border: "2px solid #dddddd",
        borderRadius: 1.75,
        mt: 2,
        pt: 2
      }}>
        <Grid container spacing={2} alignItems="stretch">
          {BOOKING_DETAILS_SECTIONS.map((section) => {
            const visibleFields = section.fields.filter(
              (key) => data?.[key] !== undefined && !shouldExcludeField(key),
            );
            if (visibleFields.length === 0) return null;

            return (
              <Grid item xs={12} md={4} key={section.title}>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 1,
                    // backgroundColor: alpha(theme.palette.grey[500], 0.04),
                    // border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <SectionHeader title={section.title} />
                  <Box sx={{ flex: 1 }}>
                    {visibleFields.map((key) => {
                      const Icon = getFieldIcon(key);
                      const formattedLabel = formatFieldName(key);
                      const formattedValue = formatFieldValue(
                        key,
                        data[key],
                        ftDate,
                      );

                      return (
                        <InfoItem
                          key={key}
                          icon={<Icon sx={{ fontSize: 16 }} />}
                          label={formattedLabel}
                          value={formattedValue}
                        />
                      );
                    })}
                  </Box>
                </Box>
              </Grid>
            );
          })}
        </Grid>

        <Box sx={{
          position: 'absolute',
          top: -2,
          left: -1,
        }}>
          <LeadStatusRenderer
            status={data?.status || ""}
            actualStatus={data?.actual_status || ""}
          />
        </Box>
      </Box>

      {openCancellation && data?.leads_uuid && (
        <PaymentDetailsDialog
          open={openCancellation}
          leadUUID={data.leads_uuid}
          onClose={() => setOpenCancellation(false)}
          onComplete={(isSuccess, cancellationData) =>
            handleCancellationComplete(!!isSuccess, cancellationData)
          }
          isAdvance={true}
          isCancellation={true}
        />
      )}
    </RightPanel>
  );
};
